import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row } from 'antd'
import { Formik, FormikProps, FormikValues } from 'formik'
import * as Yup from 'yup'

import { ApplicationInfo } from 'core/application/ichraDecisionTool/prospectSignup/state'
import question3Api from 'core/application/ichraDecisionTool/prospectSignup/step2/question3'

import Tooltip from 'common/components/dataDisplay/Tooltip'
import Radio from 'common/components/dataEntry/formik/FormikRadioField'
import notification from 'common/components/feedback/Notification'
import Button from 'common/components/general/Button'

import { commandRequest } from 'common/RequestUtils'
import { BaseNavigationProps } from 'common/wizard/BaseNavigationProps'

import CategoryItemPageHeader from '../common/CategoryItemPageHeader'

import '../SignupStyles.less'

const formValidator = (translate: any) =>
  Yup.object({
    powerToChooseImportance: Yup.number().min(1, translate('required')).required(translate('required')),
  })

export type Step2Question3Props = Pick<ApplicationInfo, 'powerToChooseImportance' | 'powerToChooseImportanceOptions'>

const Question3: React.FC<BaseNavigationProps<Step2Question3Props>> = (props) => {
  const { t: translate } = useTranslation('ichra-decision-tool')
  const [loading, setLoading] = useState<boolean>(false)

  const onSubmit = async (values: Step2Question3Props) => {
    setLoading(true)
    const res = await commandRequest(() => question3Api(values.powerToChooseImportance))
    setLoading(false)
    if (!res.errors) {
      props.goNext(props.name)
    } else {
      console.log(res.errors)
      notification.open({
        message: res.errors[0],
        type: 'error',
      })
    }
  }

  const getNextButton = (formik: FormikProps<Step2Question3Props>): ReactNode => {
    const nextButton = (
      <Button
        icon="fa fa-arrow-right"
        iconPosition="right"
        size="large"
        htmlType="submit"
        type="primary"
        disabled={!formValidator(translate).isValidSync(formik.values)}
        className="float-right next__button"
        loading={loading}
      >
        {translate('next')}
      </Button>
    )

    const missingInput = !formValidator(translate).isValidSync(formik.values)

    return missingInput ? (
      <Tooltip title={translate('next-tooltip')} position="left">
        {nextButton}
      </Tooltip>
    ) : (
      nextButton
    )
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={props.state}
      validationSchema={formValidator(translate)}
      onSubmit={onSubmit}
      validate={(values) => {
        props.hasDirtyData(true)
      }}
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit} autoComplete="off" onBlur={formik.handleBlur}>
          <Row>
            <Col xs={24} sm={24} md={13}>
              <CategoryItemPageHeader
                title={translate('prospect-signup.step-2.question-1-title')}
                numberOfQuestions={6}
                currentQuestion={3}
                question={translate('prospect-signup.step-2.question-3')}
                className="ml-0"
                titleFontSize={20}
              />
            </Col>
          </Row>

          <Row className="form__row">
            <Col xs={24} sm={24} md={12}>
              <Radio.Group name="powerToChooseImportance" defaultValue={props.state?.powerToChooseImportance}>
                <Row>
                  <Col className="flex__space_between">
                    {props.state.powerToChooseImportanceOptions.map((option) => (
                      <div className="full-width options pr-8 pl-0">
                        <Radio.Button value={option}>{option}</Radio.Button>
                      </div>
                    ))}
                  </Col>
                </Row>
                <div className="flex__space_between">
                  <p className="color-gray-8">{translate('not-important')}</p>
                  <p className="color-gray-8">{translate('very-important')}</p>
                </div>
              </Radio.Group>
            </Col>
          </Row>

          <Row>
            <Col xs={24}>
              <div className="bottom__row">
                <Button
                  icon="fa fa-arrow-left"
                  size="large"
                  htmlType="button"
                  type="default"
                  onClick={() => {
                    props.goBack(props.name)
                  }}
                >
                  {translate('back')}
                </Button>
                {getNextButton(formik)}
              </div>
            </Col>
          </Row>
        </form>
      )}
    </Formik>
  )
}

export default Question3
